/**
 * Header — sticky top bar for all authenticated pages.
 *
 * Left:   mobile hamburger + page title (derived from route)
 * Right:  ThemeToggle + UserMenu dropdown
 *
 * The header lives inside the content column so its width automatically
 * tracks the sidebar's collapse state without any extra calculation.
 */

import { useState, useRef, useEffect } from 'react'
import { Link, useLocation }           from 'react-router-dom'
import { motion, AnimatePresence }     from 'framer-motion'
import {
  Menu,
  ChevronDown,
  UserCog,
  ShieldCheck,
  LogOut,
  Loader2,
} from 'lucide-react'

import { ThemeToggle }              from '@theme'
import { useUiStore, useAuthStore } from '@store'
import { useLogoutMutation }        from '@features/auth'
import { NotificationBell }        from '@features/notifications'
import { ROUTES }                   from '@constants/routes'
import { BRANDING }                 from '@constants/branding'
import { AppLogo }                  from '@components/brand'
import { UserAvatarCircle }         from '@components/ui/UserAvatarCircle'
import { cn }                       from '@utils'

// ── Page title ────────────────────────────────────────────────────────────────

const STATIC_TITLES = {
  // Admin console
  [ROUTES.ADMIN.DASHBOARD]:          'Dashboard',
  [ROUTES.ADMIN.EVENTS.LIST]:        'Events',
  [ROUTES.ADMIN.EVENTS.CREATE]:      'Create Event',
  [ROUTES.ADMIN.USERS.LIST]:         'Users',
  [ROUTES.ADMIN.USERS.CREATE]:       'Add User',
  [ROUTES.ADMIN.REPORTS.OVERVIEW]:   'Reports',
  [ROUTES.ADMIN.REPORTS.EVENTS]:     'Event Reports',
  [ROUTES.ADMIN.SETTINGS.PROFILE]:       'Profile',
  [ROUTES.ADMIN.SETTINGS.ACCOUNT]:       'Account & Password',
  [ROUTES.ADMIN.SETTINGS.ORGANIZATION]:  'Organization',
  [ROUTES.ADMIN.SETTINGS.BRANDING]:      'Branding',
  [ROUTES.ADMIN.SETTINGS.REGIONAL]:      'Regional',
  [ROUTES.ADMIN.SETTINGS.NOTIFICATIONS]: 'Notifications',
  [ROUTES.ADMIN.SETTINGS.SECURITY]:      'Security',
  // User portal
  [ROUTES.PORTAL.DASHBOARD]:         'Dashboard',
  [ROUTES.PORTAL.MY_EVENTS]:         'My Events',
  [ROUTES.PORTAL.PROFILE]:           'Profile',
  [ROUTES.PORTAL.SETTINGS]:          'Settings',
  [ROUTES.PORTAL.NOTIFICATIONS]:     'Notifications',
  [ROUTES.PORTAL.SETTINGS_PAGES.NOTIFICATIONS]: 'Notification Preferences',
  // Admin notifications
  [ROUTES.ADMIN.NOTIFICATIONS.LIST]:    'Notifications',
  [ROUTES.ADMIN.NOTIFICATIONS.HISTORY]: 'Notification History',
  // Communication
  [ROUTES.ADMIN.COMMUNICATION.TEMPLATES]: 'Email Templates',
}

function usePageTitle() {
  const { pathname } = useLocation()

  const title = (() => {
    if (STATIC_TITLES[pathname]) return STATIC_TITLES[pathname]
    // Admin dynamic segments
    if (/^\/admin\/events\/[^/]+\/edit/.test(pathname))                return 'Edit Event'
    if (/^\/admin\/events\/[^/]+/.test(pathname))                      return 'Event Details'
    if (/^\/admin\/users\/[^/]+\/edit/.test(pathname))                 return 'Edit User'
    if (/^\/admin\/users\/[^/]+/.test(pathname))                       return 'User Details'
    if (/^\/admin\/assignments/.test(pathname))                        return 'Events'
    if (/^\/admin\/registrations/.test(pathname))                      return 'Events'
    // Portal dynamic segments
    if (/^\/portal\/events\/[^/]+/.test(pathname))                     return 'Event Details'
    // Communication routes
    if (/^\/admin\/communication\/templates\/[^/]+/.test(pathname))   return 'Edit Template'
    // Legacy notification template redirect (transitional)
    if (/^\/admin\/notifications\/templates\/[^/]+/.test(pathname))   return 'Edit Template'
    return BRANDING.APP_NAME
  })()

  // Keep browser tab title in sync: "After Summit | Dashboard"
  useEffect(() => {
    document.title = BRANDING.pageTitle(title !== BRANDING.APP_NAME ? title : '')
  }, [title])

  return title
}

// ── User menu ─────────────────────────────────────────────────────────────────

const menuVariants = {
  hidden:  { opacity: 0, scale: 0.95, y: -8, transition: { duration: 0.12, ease: 'easeIn'  } },
  visible: { opacity: 1, scale: 1,    y:  0, transition: { duration: 0.18, ease: [0.34, 1.56, 0.64, 1] } },
}

function UserMenu() {
  const user                          = useAuthStore((s) => s.user)
  const { mutate: logout, isPending } = useLogoutMutation()
  const [open, setOpen]               = useState(false)
  const containerRef                  = useRef(null)
  const { pathname }                  = useLocation()

  // Section-aware account links: portal context → portal pages, else admin settings.
  const inPortal      = pathname.startsWith(ROUTES.PORTAL.ROOT)
  const profileHref   = inPortal ? ROUTES.PORTAL.PROFILE  : ROUTES.ADMIN.SETTINGS.PROFILE
  const accountHref   = inPortal ? ROUTES.PORTAL.SETTINGS : ROUTES.ADMIN.SETTINGS.ACCOUNT
  const accountLabel  = inPortal ? 'Settings' : 'Account Settings'

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function onPointerDown(e) {
      if (!containerRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function onKeyDown(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  function close() { setOpen(false) }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Open user menu"
        className={cn(
          'flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl',
          'transition-colors duration-150 outline-none',
          'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)]',
          'hover:bg-[var(--ac-bg-subtle)]',
        )}
      >
        <UserAvatarCircle user={user} size={32} />

        {/* Name — visible on sm+ */}
        <span
          className="hidden sm:block text-sm font-medium max-w-[120px] truncate"
          style={{ color: 'var(--ac-text-base)' }}
        >
          {user?.name ?? 'User'}
        </span>

        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center"
          style={{ color: 'var(--ac-text-subtle)' }}
        >
          <ChevronDown size={14} strokeWidth={2.5} />
        </motion.span>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            aria-label="User menu"
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="absolute right-0 top-full mt-2 w-56 rounded-2xl overflow-hidden"
            style={{
              background: 'var(--ac-bg-surface)',
              border:     '1px solid var(--ac-border-base)',
              boxShadow:  'var(--ac-shadow-xl)',
              zIndex:     400,
            }}
          >
            {/* Brand strip */}
            <div
              className="flex items-center gap-3 px-4 py-3"
              style={{ borderBottom: '1px solid var(--ac-border-base)', background: 'var(--ac-bg-subtle)' }}
            >
              <AppLogo variant="icon" height={32} style={{ borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }} />
              <span
                className="text-sm font-semibold tracking-tight"
                style={{ color: 'var(--ac-text-muted)', userSelect: 'none' }}
              >
                After <span style={{ color: 'var(--ac-secondary)' }}>Summit</span>
              </span>
            </div>

            {/* User info */}
            <div
              className="px-4 py-3"
              style={{ borderBottom: '1px solid var(--ac-border-base)' }}
            >
              <p
                className="text-sm font-semibold truncate"
                style={{ color: 'var(--ac-text-base)' }}
              >
                {user?.name ?? 'User'}
              </p>
              {user?.email && (
                <p
                  className="text-xs truncate mt-0.5"
                  style={{ color: 'var(--ac-text-muted)' }}
                >
                  {user.email}
                </p>
              )}
            </div>

            {/* Navigation links */}
            <div className="p-1">
              <Link
                role="menuitem"
                to={profileHref}
                onClick={close}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-xl text-sm w-full',
                  'transition-colors duration-150 outline-none',
                  'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)] focus-visible:ring-inset',
                  'hover:bg-[var(--ac-bg-subtle)]',
                )}
                style={{ color: 'var(--ac-text-muted)' }}
              >
                <UserCog size={15} strokeWidth={1.75} />
                Profile
              </Link>

              <Link
                role="menuitem"
                to={accountHref}
                onClick={close}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-xl text-sm w-full',
                  'transition-colors duration-150 outline-none',
                  'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)] focus-visible:ring-inset',
                  'hover:bg-[var(--ac-bg-subtle)]',
                )}
                style={{ color: 'var(--ac-text-muted)' }}
              >
                <ShieldCheck size={15} strokeWidth={1.75} />
                {accountLabel}
              </Link>
            </div>

            {/* Divider */}
            <div className="mx-1" style={{ height: '1px', background: 'var(--ac-border-base)' }} />

            {/* Sign out */}
            <div className="p-1">
              <button
                role="menuitem"
                type="button"
                onClick={() => { logout(); close() }}
                disabled={isPending}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-xl text-sm w-full text-left',
                  'transition-colors duration-150 outline-none disabled:opacity-50',
                  'focus-visible:ring-2 focus-visible:ring-[var(--ac-danger)] focus-visible:ring-inset',
                  'hover:bg-[var(--ac-danger-bg)]',
                )}
                style={{ color: 'var(--ac-danger)' }}
              >
                {isPending
                  ? <Loader2 size={15} className="animate-spin" />
                  : <LogOut  size={15} strokeWidth={1.75} />
                }
                {isPending ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Main export ───────────────────────────────────────────────────────────────

export function Header() {
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen)
  const title          = usePageTitle()

  return (
    <header
      className="sticky top-0 z-[100] flex items-center gap-3 h-16 px-4 sm:px-6 flex-shrink-0"
      style={{
        background:   'var(--ac-bg-surface)',
        borderBottom: '1px solid var(--ac-border-base)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      {/* Mobile: open sidebar drawer */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open navigation"
        className={cn(
          'lg:hidden flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0',
          'transition-colors duration-150 outline-none',
          'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)]',
          'hover:bg-[var(--ac-bg-subtle)]',
        )}
        style={{ color: 'var(--ac-text-muted)' }}
      >
        <Menu size={20} strokeWidth={1.75} />
      </button>

      {/* Mobile-only brand logo — visible when sidebar is off-canvas */}
      <div className="lg:hidden flex-shrink-0">
        <AppLogo
          variant="icon"
          height={44}
          style={{ borderRadius: '10px', overflow: 'hidden' }}
        />
      </div>

      {/* Page title */}
      <h1
        className="font-semibold text-base truncate flex-1"
        style={{
          fontFamily: 'var(--ac-font-display)',
          color:      'var(--ac-text-base)',
        }}
      >
        {title}
      </h1>

      {/* Right controls */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <ThemeToggle variant="icon" />

        {/* Notification Bell */}
        <NotificationBell />

        {/* Divider */}
        <div
          className="w-px h-5 mx-1"
          style={{ background: 'var(--ac-border-base)' }}
        />

        <UserMenu />
      </div>
    </header>
  )
}
